import { Box, Cell, Layout, Text } from '@wix/design-system';
import { ActionButton } from '@wix/package-picker-components';
import classnames from 'classnames';
import { useEnterpriseModalContext } from 'providers/EnterpriseModalContextProvider';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { buildEnterpriseUrlLink } from 'utils';
import { EnterpriseLanguageToFormURL } from '../../../constants';
import { EnterpriseFeaturesItemData } from '../../../pages/PlansPage/Enterprise/EnterpriseFeatures';
import s from './EnterpriseBanner.scss';
import { EnterpriseBannerFeatureItem } from './EnterpriseBannerFeatureItem';
import { PurchaseMode } from 'enums';

const EnterpriseFeaturesItemsData: EnterpriseFeaturesItemData[] = [
  {
    icon: 'banner-deployment',
    title: 'premium-purchase-plan.enterprise_banner.content_deployment_title',
    body: 'premium-purchase-plan.enterprise_banner.content_deployment_subtitle',
  },
  {
    icon: 'banner-management',
    title:
      'premium-purchase-plan.enterprise_banner.multi-site_management.title',
    body: 'premium-purchase-plan.enterprise_banner.multi-site_management.subtitle',
  },
  {
    icon: 'banner-integration',
    title: 'premium-purchase-plan.enterprise_banner.custom_integrations.title',
    body: 'premium-purchase-plan.enterprise_banner.custom_integrations.subtitle',
  },
  {
    icon: 'banner-support',
    title:
      'premium-purchase-plan.enterprise_banner.dedicated_account_support.title',
    body: 'premium-purchase-plan.enterprise_banner.dedicated_account_support.subtitle',
  },
];

interface EnterpriseBannerProps {
  locale: string;
  userGuid?: string;
  clientId?: string;
  isReplaceChangePlanPurchaseModeEnabled: boolean;
  isUnifiedChangePlanPhase1Enabled: boolean;
}

export const EnterpriseBanner: React.FC<EnterpriseBannerProps> = ({
  locale,
  userGuid,
  clientId,
  isReplaceChangePlanPurchaseModeEnabled,
  isUnifiedChangePlanPhase1Enabled,
}) => {
  const { t } = useTranslation();
  const { openModal } = useEnterpriseModalContext();
  const { purchaseMode, isChangePlan } = usePageModeContext();

  const handleCTAOnClick = () => {
    const url = EnterpriseLanguageToFormURL.get(locale);

    if (url) {
      const enterpriseUrlLink = buildEnterpriseUrlLink({
        localeUrl: url,
        referrer: 'wix-pp-main',
        locale,
        userGuid,
        clientId,
      });
      window.open(enterpriseUrlLink, '_blank');
    } else {
      openModal('banner');
    }
  };

  const purchaseModeClassName =
    isChangePlan &&
    isReplaceChangePlanPurchaseModeEnabled &&
    !isUnifiedChangePlanPhase1Enabled
      ? s[PurchaseMode.CHANGE_PLAN]
      : s[purchaseMode];

  return (
    <Box
      className={classnames(s.enterpriseBanner, purchaseModeClassName)}
      gap={6}
    >
      <Box className={s.content} gap='6px' direction='vertical'>
        <Text weight='bold' className={s.title}>
          {t('premium-purchase-plan.enterprise_banner.title')}
        </Text>
        <Text size='medium'>
          {t('premium-purchase-plan.enterprise_banner.subtitle')}
        </Text>
        <Box marginTop='SP3'>
          <ActionButton onClick={handleCTAOnClick}>
            {t('premium-purchase-plan.enterprise_banner.CTA')}
          </ActionButton>
        </Box>
      </Box>
      <Box className={s.features}>
        <Layout gap='42px'>
          {EnterpriseFeaturesItemsData.map((data) => (
            <Cell span={6} key={data.title}>
              <EnterpriseBannerFeatureItem
                {...data}
                isReplaceChangePlanPurchaseModeEnabled={
                  isReplaceChangePlanPurchaseModeEnabled
                }
                isUnifiedChangePlanPhase1Enabled={
                  isUnifiedChangePlanPhase1Enabled
                }
              />
            </Cell>
          ))}
        </Layout>
      </Box>
    </Box>
  );
};
